import * as THREE from "three";
import { hash, lerp, TAU } from "../systems/math";
import { tokens } from "../brand/tokens";
import type { QualityPreset } from "../systems/quality";

/** Normalized targets for CANNAC lettermark crystallize. */
const LETTER_OFFSETS: [number, number, number][] = (() => {
  const pts: [number, number, number][] = [];
  const letters = ["C", "A", "N", "N", "A", "C"];
  const spacing = 0.42;
  const startX = -((letters.length - 1) * spacing) / 2;

  letters.forEach((ch, li) => {
    const cx = startX + li * spacing;
    const count = ch === "N" ? 18 : 14;
    for (let i = 0; i < count; i += 1) {
      const a = (i / count) * TAU;
      const r = ch === "C" ? 0.22 + (i % 3) * 0.02 : 0.18;
      const y = Math.sin(a * 2) * 0.12;
      const z = (hash(li * 17 + i) - 0.5) * 0.08;
      pts.push([cx + Math.cos(a) * r, y + (hash(i + li) - 0.5) * 0.06, z]);
    }
  });
  return pts;
})();

type Particle = {
  x: number;
  y: number;
  z: number;
  tx: number;
  ty: number;
  tz: number;
  phase: number;
};

export class LettermarkField {
  readonly group = new THREE.Group();
  private mesh: THREE.InstancedMesh | null = null;
  private particles: Particle[] = [];
  private dummy = new THREE.Object3D();
  private time = 0;
  private crystallize = 0;

  constructor(quality: QualityPreset) {
    this.group.name = "Lettermark";
    if (quality.particleScale <= 0) return;

    const scale = quality.tier === "B" ? 0.7 : 1;
    const budget = Math.floor(LETTER_OFFSETS.length * scale);

    this.particles = LETTER_OFFSETS.slice(0, budget).map((t, i) => {
      const spread = 2.5;
      return {
        x: (hash(i * 2.1) - 0.5) * spread,
        y: (hash(i * 3.3) - 0.5) * spread + 1.2,
        z: (hash(i * 5.7) - 0.5) * spread,
        tx: t[0],
        ty: t[1] + 0.35,
        tz: t[2],
        phase: hash(i * 9.1) * TAU,
      };
    });

    const geo = new THREE.SphereGeometry(1, 6, 6);
    const mat = new THREE.MeshStandardMaterial({
      color: tokens.fog,
      emissive: tokens.leaf,
      emissiveIntensity: 0.4,
      roughness: 0.3,
      metalness: 0.15,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, this.particles.length);
    this.group.add(this.mesh);
    this.group.position.set(0, 0.05, 0);
  }

  setCrystallize(t: number) {
    this.crystallize = t;
    const mat = this.mesh?.material as THREE.MeshStandardMaterial | undefined;
    if (mat) mat.emissiveIntensity = 0.15 + t * 0.65;
  }

  update(dt: number) {
    if (!this.mesh) return;
    this.time += dt;
    const ease = this.crystallize;

    this.particles.forEach((p, i) => {
      const u = ease * (0.82 + 0.18 * Math.sin(this.time * 1.4 + p.phase));
      p.x = lerp(p.x, p.tx, u * 0.08 + 0.04);
      p.y = lerp(p.y, p.ty, u * 0.08 + 0.04);
      p.z = lerp(p.z, p.tz, u * 0.08 + 0.04);

      const pulse = 0.02 + 0.01 * Math.sin(this.time * 3 + p.phase);
      this.dummy.position.set(p.x, p.y, p.z);
      this.dummy.scale.setScalar(pulse * (0.6 + ease * 0.5));
      this.dummy.updateMatrix();
      this.mesh!.setMatrixAt(i, this.dummy.matrix);
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose() {
    this.mesh?.geometry.dispose();
    (this.mesh?.material as THREE.Material | undefined)?.dispose();
  }
}
